"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  number: "01",
  question: "How long does it take to design a new website?",
  answer: "For most small-to-medium businesses, our design process typically spans 4 to 6 weeks. This includes discovery, wireframing, development, and final optimization to ensure your site is fast, responsive, and ready to convert visitors into loyal customers."
}, {
  number: "02",
  question: "Can you help if my Google Business Profile is suspended?",
  answer: "Yes, we specialize in resolving Google Listing issues. We will audit your profile, identify the cause of the suspension, and handle the reinstatement process with Google support to get your business back on the map as quickly as possible."
}, {
  number: "03",
  question: "What is the difference between SEO and Local SEO?",
  answer: "Standard SEO focuses on ranking your website globally for specific keywords. Local SEO is hyper-focused on your geographic area, ensuring you appear in the 'Map Pack' and local search results when nearby customers search for services like yours."
}, {
  number: "04",
  question: "Do you provide ongoing maintenance for websites?",
  answer: "We offer flexible maintenance packages to keep your site secure, updated, and performing at its peak. From plugin updates to content adjustments, we handle the technical side so you can focus on running your business."
}, {
  number: "05",
  question: "How do you measure the success of an SEO campaign?",
  answer: "We provide transparent monthly reports tracking key metrics such as organic traffic growth, keyword rankings, and conversion rates. Our goal is to show you a clear return on investment through increased leads and sales."
}, {
  number: "06",
  question: "Is my business too small for professional marketing?",
  answer: "Not at all. Asahi Search is specifically designed to help small-to-medium businesses compete with larger players. We create scalable, cost-effective strategies that deliver high impact without the enterprise-level price tag."
}, {
  number: "07",
  question: "Can you work with my existing website?",
  answer: "Absolutely. We can perform a comprehensive audit of your current site to identify performance bottlenecks and SEO gaps. We then provide a roadmap to either optimize your existing platform or rebuild it for better results."
}, {
  number: "08",
  question: "How do I get started with Asahi Search?",
  answer: "Getting started is simple. Reach out through our contact form to schedule a free discovery call. We will discuss your business goals, analyze your current online presence, and outline a strategy tailored to your specific needs."
}];
export default function WebsiteDesignFaq() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const list = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3235" id="website-design-faq" className="bg-background py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-muted-foreground mb-6">— FAQ / WEBSite Design</p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-foreground tracking-tight">
              Common questions,{" "}
              <span className="italic font-light">clearly</span> answered.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/contact-us" className="font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 inline-flex items-center gap-2 border-b border-foreground/30 hover:border-primary pb-1">
              Still have questions? Contact us →
            </Link>
          </div>
        </div>

        {}
        <div ref={list.ref} className={`max-w-[880px] mx-auto transition-all duration-700 ease-out delay-150 ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group border-b border-border last:border-b-0 data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300">
                <AccordionTrigger className="flex items-start justify-between gap-6 py-7 md:py-8 hover:no-underline [&>svg]:hidden text-left">
                  <div className="flex items-start gap-6 md:gap-10 flex-1">
                    <span className="font-mono text-xs md:text-sm text-muted-foreground pt-2 shrink-0 tracking-wider">
                      {faq.number}
                    </span>
                    <span className="font-default font-semibold text-lg md:text-[22px] leading-snug text-foreground">
                      {faq.question}
                    </span>
                  </div>
                  <Plus className="size-5 md:size-6 text-primary shrink-0 mt-2 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45" strokeWidth={2} />
                </AccordionTrigger>
                <AccordionContent className="pl-12 md:pl-[72px] pr-10 md:pr-12 pb-8">
                  <p className="font-default text-base text-muted-foreground leading-[1.8]">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}